import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CaseActivitiesManager from '../activities/CaseActivitiesManager';

/**
 * Add Activity from the case page — the case-manager counterpart to
 * scale-portal's Case Actions button.
 *
 * WHY THE CASE IS LOCKED HERE
 * ---------------------------
 * Opened from a case, the case is already known. The Schedule page offers a
 * searchable picker because it starts from a mediator and a date; here that
 * would only invite the case manager to attach the activity to a DIFFERENT
 * case than the one they are looking at.
 *
 * THE MEDIATOR COMES FROM THE CASE, NOT FROM A SELECTOR
 * ----------------------------------------------------
 * `caseData.mediator.userId` is the auth uuid that owns the case, which is the
 * same id `mediator_activities.mediator_id` holds. So the activity lands on
 * THAT mediator's calendar — not the case manager's, and not whichever mediator
 * happened to be selected on the Schedule page.
 *
 * THE GATE IS PER MEDIATOR
 * ------------------------
 * Asked per case, because a case manager's mediators do not all have the
 * feature. Fails closed: anything other than a clean yes hides the button, so a
 * mediator without it — or one this case manager is not assigned — simply does
 * not see it.
 */
const AddActivity = ({ caseData, onSaved }) => {
  const mediatorId = caseData?.mediator?.userId || caseData?.mediator?.user_id;
  const caseId = caseData?.id;
  const caseName = caseData?.caseName || caseData?.case_name;

  /* Resolved server-side on the case payload, exactly as scale-portal does, so
   * the button paints with the other Case Actions instead of a beat later.
   * Strict === true; fails closed. */
  const enabled = caseData?.activity_available === true;
  const [isOpen, setIsOpen] = useState(false);

  /* ARRIVING FROM AN ACTIVITY ON THE SCHEDULE BOARD.
   *
   * The chip navigates here with `?focus=activities`, and this scrolls the
   * button into view and pulses it — it does NOT open the dialog.
   *
   * Danish, 13 Oct, arguing against opening it on arrival: "it scrolls to that
   * section and highlights it, and that's it. It doesn't force you to look at
   * it immediately. But if you want them to immediately open that pop-up,
   * first-timers will still not figure out where this pop-up came from… I want
   * to make sure they understand this pop-up is coming from this button."
   *
   * ONCE, and it must not cancel itself: a ref guards the single run and the
   * timers are cleared only on unmount. Listing `location.search` in a cleanup
   * while also stripping the URL would cancel the scroll before it fired.
   */
  const buttonRef = useRef(null);
  const [highlighted, setHighlighted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const didFocus = useRef(false);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (didFocus.current) return;
    if (new URLSearchParams(location.search).get('focus') !== 'activities') return;
    didFocus.current = true;
    timers.current.push(
      /* The case page is still laying out its panels when this mounts, so
         scrolling now lands on the wrong offset. */
      setTimeout(() => {
        buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlighted(true);
        navigate(location.pathname, { replace: true });
      }, 350),
      setTimeout(() => setHighlighted(false), 3400)
    );
  }, [location.search, location.pathname, navigate]);

  if (!enabled) return null;

  return (
    <>
      {/* Raw keyframes: this build does not reliably emit arbitrary Tailwind
          animation utilities, and a class that is not emitted renders as
          nothing at all. */}
      <style>{`
        @keyframes cm-activity-focus {
          0%, 100% { box-shadow: 0 0 0 0 rgba(31,92,80,0); }
          30%      { box-shadow: 0 0 0 4px rgba(31,92,80,.28); }
        }
      `}</style>
      <button
        ref={buttonRef}
        style={
          highlighted
            ? { borderColor: '#1F5C50', animation: 'cm-activity-focus 1.1s ease-in-out 2' }
            : undefined
        }
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
      >
        {/* "Activities", not "Add Activity": it opens a list you can edit,
            delete and add from — matching scale-portal. */}
        <span className="min-w-0 truncate">Activities</span>
        {/* Stroked, not filled: a filled 24x24 path renders as a grey block at
            this size. */}
        <span className="h-5 w-5 flex-shrink-0 text-gray-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-full w-full"
          >
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4M12 14v4M10 16h4" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[100] flex justify-center overflow-y-auto py-8">
          <div
            /* FIXED, not absolute: this sits inside the scrolling overlay, and
               `absolute inset-0` only covers that container's first viewport —
               scroll a tall dialog up and the dim stops, showing the page
               underneath through the gap. */
            className="fixed inset-0 bg-black/40"
            onClick={() => setIsOpen(false)}
          />
          <div className="relative z-10 my-auto w-[670px] max-w-[94vw] rounded-xl bg-white p-6 shadow-2xl">
            <CaseActivitiesManager
              mediatorId={mediatorId}
              caseId={caseId}
              caseName={caseName}
              onClose={() => setIsOpen(false)}
              onChanged={onSaved}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AddActivity;
